import { SyntheticEvent, useEffect, useMemo } from "react";
import { useStoreState } from "ezStore";
import { useLazyQuery } from "@apollo/client";
import moment from "moment/moment";

import { Map } from "@cofano/cck";
import { IconButton, Popover, Tooltip } from "@mui/material";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import usePopover from "components/hooks/usePopover";

import { Language } from "imports/Utils";

import styles from "view/maps/utils/Styles.json";
import { calculateCallStatus } from "view/calls/utils/CallUtils";
import ShipMarker from "view/maps/components/markers/ShipMarker";

import { CallTimesFragment } from "__generated__/view/calls/graphql/call.fragments.generated";
import { GetAllShipPositionsDocument } from "__generated__/view/ships/graphql/ship.queries.generated";
import { ShipPositionFragment } from "__generated__/view/ships/graphql/ship.fragments.generated";

interface ShipLocationPopoverProps {
	ship: {
		id: number;
		name:  string;
		position?: ShipPositionFragment | null;
		nextCall?: CallTimesFragment | null;
	}
}

const positionToLatLng = (position: Pick<ShipPositionFragment, "latitude" | "longitude">) => {
	return { lat: position.latitude!, lng: position.longitude! };
};

const ShipPositionPopover = ({ ship }: ShipLocationPopoverProps) => {
	const fromLong = useMemo(() => moment().subtract(1, "d").unix() * 1000, []);

	const [ getHistoric, { data: historic } ] = useLazyQuery(GetAllShipPositionsDocument, {
		variables: {
			shipId: ship.id,
			fromLong,
		},
	});

	const { popoverProps, onPopoverOpen, onPopoverClose } = usePopover({
		trigger: "click",
	});

	const session = useStoreState(store => store.session);

	useEffect(() => {
		if (popoverProps.open) {
			getHistoric();
		}
	}, [ popoverProps.open ] );

	const open = (event: SyntheticEvent) => {
		event.preventDefault();
		event.stopPropagation();

		if (popoverProps.open) {
			return;
		}

		onPopoverOpen(event);
	};

	const close = (_event: SyntheticEvent) => {
		onPopoverClose();
	};

	const hasCoordinates = ship.position != null && (ship.position.latitude !== 0 && ship.position.longitude !== 0);

	return (
		<div style={{ display: "flex", gap: 8 }}>
			<div onClick={(e) => open(e)}>
				{ship.position && hasCoordinates ? (
					<>
						<Popover
							{...popoverProps}
							open={popoverProps.open}
							onClose={close}
							slotProps={{
								paper: {
									style: { pointerEvents: "auto" },
								},
							}}>
							<div style={{ width: 500, height: 500, overflow: "hidden" }}>
								<Map
									apiKey="AIzaSyAFEhlhSJ7c85rDGz1JX99veD0sshXx_xs"
									polyLines={[ {
										line: historic?.allShipPositions
											.filter(p => p.latitude && p.longitude)
											.map(positionToLatLng),
										color: "red",
									} ]}
									polyLineColor="#E50000"
									className="control-tower-map"
									markers={[ positionToLatLng(ship.position) ]}
									styles={styles}
									markerComponent={() =>
										<ShipMarker
											label={ship.name}
											lastLocationUpdate={ship.position?.timestamp}
											course={ship.position?.course}
											actual={moment().diff(moment(ship.position?.timestamp), "minutes") < 30}
											color={calculateCallStatus(ship.nextCall, session).color} />}
									maxZoom={13}
								/>
							</div>
						</Popover>

						<IconButton size="small">
							<LocationOnIcon
								fontSize="small"
								htmlColor="#428bca"
							/>
						</IconButton>
					</>
				) : (
					<Tooltip title={Language("SHIP_POSITION_UNKNOWN")}>
						<span>
							<IconButton size="small">
								<LocationOffIcon
									fontSize="small"
								/>
							</IconButton>
						</span>
					</Tooltip>
				)}
			</div>
		</div>
	);
};

export default ShipPositionPopover;
